import { computed, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, fromEvent, map, startWith, filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ThemeService {
    theme$ = new BehaviorSubject('');
    theme = signal('');

    /** A second, independent theme class composed alongside `theme` (e.g. a visual
     * skin like 'theme-pipboy'), resolved from route data the same way `theme` is.
     * Kept separate from `theme` (which selects game/content stylesheets) so a game
     * can offer more than one visual treatment without a second routing dimension. */
    visualTheme$ = new BehaviorSubject('');
    visualTheme = signal('');

    factionTheme$ = new BehaviorSubject('');
    factionTheme = signal('');

    windowSize = toSignal(
        fromEvent(window, 'resize').pipe(
            startWith(window.innerWidth),
            map(() => window.innerWidth)
        )
    );

    isDesktop = computed(() => {
        return this.windowSize() > 991;
    });

    constructor(private router: Router) {
        // Update the theme on every completed navigation by walking the primary
        // route tree. This covers cached-page restoration (back navigation) where
        // component lifecycle hooks such as ngOnInit do not re-fire.
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
            let route = this.router.routerState.root;
            let theme = 'home';
            let visualTheme = '';
            while (route) {
                const data = route.snapshot.data;
                if (data['theme'] || data['gameId']) {
                    theme = data['theme'] || data['gameId'];
                }
                if (data['visualTheme']) {
                    visualTheme = data['visualTheme'];
                }
                route = route.children.find((c) => c.outlet === 'primary') ?? null;
            }
            this.selectTheme(theme);
            this.selectVisualTheme(visualTheme);
        });
    }

    selectTheme(name: string) {
        // Only update if theme actually changed to prevent unnecessary emissions
        if (this.theme$.value !== name) {
            console.log('[ThemeService] Changing theme from', this.theme$.value, 'to', name);
            this.theme$.next(name);
            this.theme.set(name);
            // Clear faction theme on game change; game-specific services will re-apply it
            this.setFactionTheme('');
        } else {
            console.log('[ThemeService] Theme already set to', name, '- skipping');
        }
    }

    selectVisualTheme(name: string) {
        if (this.visualTheme$.value !== name) {
            this.visualTheme$.next(name);
            this.visualTheme.set(name);
        }
    }

    setFactionTheme(faction: string) {
        if (this.factionTheme$.value !== faction) {
            this.factionTheme$.next(faction);
            this.factionTheme.set(faction);
        }
    }
}
